import React, { useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { Bars3Icon, XMarkIcon, ArrowLeftOnRectangleIcon } from '@heroicons/react/24/outline'
import { BRAND_NAME } from '../../config/brand'
import { Avatar, IconButton, useClickOutside, cx } from './primitives'
import './dashboard.css'

export function Logo({ to = '/' }: { to?: string }) {
  return (
    <Link to={to} className="sf-logo" aria-label={`${BRAND_NAME} home`}>
      <img src="/saflens-logo.png" alt={BRAND_NAME} />
    </Link>
  )
}

export interface NavItem {
  label: string
  to: string
  icon?: React.ReactNode
  /** Treat as active when the current path matches exactly (default) or starts with `to`. */
  match?: 'exact' | 'prefix'
  badge?: number | string
}

const isActive = (item: NavItem, pathname: string) =>
  item.match === 'prefix' ? pathname.startsWith(item.to) : pathname === item.to

// ─── Floating top bar with centred pill navigation ────────────────────

export interface AvatarMenuItem { label: string; icon?: React.ReactNode; onClick?: () => void; to?: string; href?: string }

export function TopNav({ items, pathname, actions, user, menuItems, homeTo }: {
  items: NavItem[]
  pathname: string
  /** Round icon buttons rendered before the avatar. */
  actions?: React.ReactNode
  user?: { name?: string | null; email?: string | null; avatarUrl?: string | null; caption?: string }
  menuItems?: AvatarMenuItem[]
  homeTo?: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useClickOutside<HTMLDivElement>(open, () => setOpen(false))
  return (
    <header className="sf-topbar">
      <Logo to={homeTo} />
      <nav className="sf-pillnav" aria-label="Primary">
        {items.map(item => (
          <NavLink key={item.to} to={item.to} end={item.match !== 'prefix'} className={() => (isActive(item, pathname) ? 'on' : '')} aria-current={isActive(item, pathname) ? 'page' : undefined}>
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="sf-topbar-right">
        {actions}
        {user && (
          <div style={{ position: 'relative' }} ref={ref}>
            <button type="button" className="sf-avatar-btn" onClick={() => setOpen(o => !o)} aria-label="Account menu" aria-expanded={open}>
              <Avatar name={user.name || user.email} src={user.avatarUrl} size={42} tone={1} />
            </button>
            {open && (
              <div className="sf-menu" role="menu">
                <div className="sf-menu-head">
                  <b>{user.name || user.email || 'Account'}</b>
                  {(user.caption || (user.name && user.email)) && <span>{user.caption || user.email}</span>}
                </div>
                {menuItems?.map(m => m.to ? (
                  <Link key={m.label} to={m.to} role="menuitem" onClick={() => setOpen(false)}>{m.icon}{m.label}</Link>
                ) : m.href ? (
                  <a key={m.label} href={m.href} role="menuitem" target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>{m.icon}{m.label}</a>
                ) : (
                  <button key={m.label} type="button" role="menuitem" onClick={() => { setOpen(false); m.onClick?.() }}>{m.icon}{m.label}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  )
}

// ─── Sidebar layout (secondary listing pages) ─────────────────────────

export interface SideSection { label?: string; items: NavItem[] }

export function SidebarLayout({ sections, pathname, onLogout, footer, children, topRight }: {
  sections: SideSection[]
  pathname: string
  onLogout?: () => void
  footer?: React.ReactNode
  children: React.ReactNode
  /** Actions in the mobile top bar (and nothing on desktop). */
  topRight?: React.ReactNode
}) {
  const [drawer, setDrawer] = useState(false)
  const nav = (onNavigate?: () => void) => (
    <>
      <Logo to={sections[0]?.items[0]?.to} />
      {sections.map((s, i) => (
        <div key={i}>
          {s.label && <div className="sf-side-label">{s.label}</div>}
          {s.items.map(item => (
            <NavLink key={item.to} to={item.to} end={item.match !== 'prefix'} onClick={onNavigate} className={() => cx('sf-side-link', isActive(item, pathname) && 'on')} aria-current={isActive(item, pathname) ? 'page' : undefined}>
              {item.icon}{item.label}
              {item.badge !== undefined && item.badge !== 0 && <span className="sf-badge">{item.badge}</span>}
            </NavLink>
          ))}
        </div>
      ))}
      <div className="sf-side-foot">
        {footer}
        {onLogout && (
          <button type="button" className="sf-side-link" onClick={onLogout}>
            <ArrowLeftOnRectangleIcon />Log out
          </button>
        )}
      </div>
    </>
  )
  const allItems = sections.flatMap(s => s.items)
  return (
    <div className="sf-sidelayout">
      <aside className="sf-side" aria-label="Sidebar"><div className="sf-side-inner">{nav()}</div></aside>
      <div className="sf-side-main">
        <div className="sf-container" style={{ paddingBottom: 0 }}>
          <header className="sf-topbar sf-mobilebar">
            <Logo to={allItems[0]?.to} />
            <div className="sf-topbar-right">
              {topRight}
              <IconButton label={drawer ? 'Close menu' : 'Open menu'} onClick={() => setDrawer(d => !d)}>
                {drawer ? <XMarkIcon /> : <Bars3Icon />}
              </IconButton>
            </div>
            <nav className="sf-pillnav" aria-label="Primary">
              {allItems.map(item => (
                <NavLink key={item.to} to={item.to} end={item.match !== 'prefix'} className={() => (isActive(item, pathname) ? 'on' : '')}>{item.label}</NavLink>
              ))}
            </nav>
          </header>
          {drawer && (
            <div className="sf-card sf-fade" style={{ marginTop: -12, marginBottom: 20 }}>
              {nav(() => setDrawer(false))}
            </div>
          )}
        </div>
        {children}
      </div>
    </div>
  )
}

// ─── Header pieces ────────────────────────────────────────────────────

export function HeaderStats({ items }: { items: Array<{ label: string; value: React.ReactNode; icon?: React.ReactNode }> }) {
  return (
    <div className="sf-headstats">
      {items.map(i => (
        <div className="sf-headstat" key={i.label}>
          <div className="sf-headstat-v">
            {i.icon ?? <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h12M4 10h12M4 14h8" /></svg>}
            <span>{i.value}</span>
          </div>
          <div className="sf-headstat-l">{i.label}</div>
        </div>
      ))}
    </div>
  )
}

export function PageHead({ title, sub, actions }: { title: React.ReactNode; sub?: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div className="sf-head">
      <div style={{ minWidth: 0 }}>
        <h1 className="sf-hello">{title}</h1>
        {sub && <p className="sf-hello-sub">{sub}</p>}
      </div>
      {actions && <div className="sf-head-actions">{actions}</div>}
    </div>
  )
}
